import React from 'react';
import { 
  Monitor, 
  Users, 
  Building2, 
  Building, 
  Landmark,
  BriefcaseIcon 
} from 'lucide-react';

const CategoryCard = ({ category, isSelected, onSelect, jobCount }) => {
  // Map of category icons
  const categoryIcons = {
    'it': Monitor,
    'non-it': Users,
    'walk-in': Building2,
    'off-campus': Building,
    'government': Landmark,
    'all': BriefcaseIcon
  };

  // Format category name for display
  const formatCategoryName = (name) => {
    if (name === 'it') return 'IT';
    return name.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get the icon component for the category
  const IconComponent = categoryIcons[category] || BriefcaseIcon;

  return (
    <button
      onClick={() => onSelect(category)}
      className={`group relative flex flex-col justify-center items-center w-full py-2 px-3 sm:py-2.5 sm:px-4
        rounded-xl transition-all duration-300 overflow-hidden backdrop-blur-sm min-h-[60px]
        ${isSelected 
          ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-lg scale-[1.02] border border-blue-400/20' 
          : 'bg-white/80 hover:bg-white text-gray-800 hover:shadow-md border border-gray-200/80 hover:border-blue-200'
        }`}
    >
      {/* Animated background gradient */}
      <div className={`absolute inset-0 transition-opacity duration-500 ${
        isSelected ? 'opacity-100' : 'opacity-0'
      }`}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 via-transparent to-white/10"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent opacity-60"></div>
      </div>

      {/* Main content container */}
      <div className="relative flex flex-col items-center gap-1">
        {/* Icon and title row */}
        <div className="flex items-center justify-center gap-2">
          {/* Icon container */}
          <div className={`relative flex items-center justify-center w-6 h-6 rounded-lg
            transition-all duration-300 group-hover:scale-110 ${
            isSelected
              ? 'bg-white/20 text-white shadow-sm'
              : 'bg-blue-50 text-blue-500 group-hover:bg-blue-100 group-hover:text-blue-600'
          }`}>
            <IconComponent className="w-3.5 h-3.5" strokeWidth={2} />
            
            {/* Animated ring */}
            <div className={`absolute inset-0 rounded-lg transition-all duration-500 ${
              isSelected
                ? 'ring-2 ring-white/40 ring-offset-1 ring-offset-blue-600'
                : 'ring-1 ring-blue-100 group-hover:ring-blue-200'
            }`}></div>
          </div>

          {/* Category name */}
          <h3 className={`text-sm font-medium transition-colors duration-300 leading-none
            ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
            {formatCategoryName(category)}
          </h3>
        </div>

        {/* Job count */}
        <p className={`text-xs transition-colors duration-300
          ${isSelected ? 'text-blue-100' : 'text-gray-500 group-hover:text-gray-600'}`}>
          {jobCount} {jobCount === 1 ? 'job' : 'jobs'}
        </p>
      </div>

      {/* Bottom highlight */}
      <div className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-500 
        ${isSelected
          ? 'bg-gradient-to-r from-white/0 via-white/50 to-white/0'
          : 'bg-transparent group-hover:bg-gradient-to-r group-hover:from-blue-500/0 group-hover:via-blue-500/20 group-hover:to-blue-500/0'
        }`}
      ></div>

      {/* Hover highlight effect */}
      <div className={`absolute inset-0 transition-opacity duration-500 pointer-events-none
        bg-gradient-to-t from-white/5 via-white/10 to-transparent opacity-0 group-hover:opacity-100
        ${isSelected ? 'opacity-100' : ''}`}
      ></div>
    </button>
  );
};

export default CategoryCard; 